/**
 * PWA Install Prompt — captures the beforeinstallprompt event and shows
 * a bottom banner inviting the user to install.
 *
 * Respects a "don't ask again" preference stored in localStorage.
 * Also exposes a manual trigger via the exported `usePWAInstall` hook
 * so other UI (e.g. a settings menu item) can invoke it.
 */
import React, { useEffect, useState, useCallback, createContext, useContext } from 'react';
import {
  Snackbar,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Slide,
} from '@mui/material';
import { GetApp, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// ----------------------------------------------------------------
// Context so any component can trigger the install prompt
// ----------------------------------------------------------------
interface PWAInstallContextValue {
  canInstall: boolean;
  isInstalled: boolean;
  promptInstall: () => Promise<void>;
}

const PWAInstallContext = createContext<PWAInstallContextValue>({
  canInstall: false,
  isInstalled: false,
  promptInstall: async () => {},
});

export const usePWAInstall = () => useContext(PWAInstallContext);

// ----------------------------------------------------------------
// localStorage key for "don't ask again"
// ----------------------------------------------------------------
const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION_DAYS = 30; // Don't ask again for 30 days

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const until = parseInt(raw, 10);
    if (Date.now() < until) return true;
    localStorage.removeItem(DISMISS_KEY);
    return false;
  } catch {
    return false;
  }
}

function setDismissed() {
  try {
    const until = Date.now() + DISMISS_DURATION_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
  } catch {
    // Ignore
  }
}

// ----------------------------------------------------------------
// Component
// ----------------------------------------------------------------
const PWAInstallPrompt: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('common');
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Detect if already installed (standalone mode)
  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    setIsInstalled(mq.matches || (navigator as any).standalone === true);
    const handler = (e: MediaQueryListEvent) => setIsInstalled(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Capture the beforeinstallprompt event
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show banner after a short delay (don't interrupt initial load)
      if (!isDismissed() && !isInstalled) {
        setTimeout(() => setShowBanner(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Detect successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowBanner(false);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isInstalled]);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
    setShowBanner(false);
  }, [deferredPrompt]);

  const handleDismiss = () => {
    setShowBanner(false);
  };

  const handleDontAskAgain = () => {
    setDismissed();
    setShowBanner(false);
  };

  const contextValue: PWAInstallContextValue = {
    canInstall: deferredPrompt !== null && !isInstalled,
    isInstalled,
    promptInstall,
  };

  return (
    <PWAInstallContext.Provider value={contextValue}>
      {children}

      {/* Bottom banner */}
      <Snackbar
        open={showBanner}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
      >
        <Paper
          elevation={6}
          sx={{
            p: 2,
            mx: 1,
            maxWidth: 480,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderRadius: 3,
          }}
        >
          <GetApp color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2">
              {t('pwa.installTitle', 'Install Saqia')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('pwa.installDescription', 'Add to home screen for the best experience and push notifications.')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
            <Button
              variant="contained"
              size="small"
              onClick={promptInstall}
              startIcon={<GetApp />}
            >
              {t('pwa.install', 'Install')}
            </Button>
            <Button
              size="small"
              color="inherit"
              onClick={handleDontAskAgain}
              sx={{ fontSize: '0.7rem', textTransform: 'none', color: 'text.secondary' }}
            >
              {t('pwa.dontAskAgain', "Don't ask again")}
            </Button>
          </Box>
          <IconButton size="small" onClick={handleDismiss} sx={{ alignSelf: 'flex-start' }}>
            <Close fontSize="small" />
          </IconButton>
        </Paper>
      </Snackbar>
    </PWAInstallContext.Provider>
  );
};

export default PWAInstallPrompt;
